import { gateway, webLib } from '@pok/weblib';
import { Alchemy, Network } from 'alchemy-sdk';
import axios from 'axios';
import { base64toBlob } from './utils/conversion.js';
import { getValidatedCertificateAccessToken, saveValidatedCertificateAccessToken } from './utils/sessionStorage.js';
import {
    alchemyProofsOf,
    getCertificatesFromUser,
    getCertificateWithCustomAxios,
    getDomainDetails,
    legacyApplyGateway as applyGateway,
    legacyApproveCertificateIssuance,
    LegacyCertificateTrackAction as CertificateTrackAction,
    legacyChangeRequestCommit,
    legacyChangeRequestDraft,
    legacyForgotCertificatePassword,
    legacyGetCertificate,
    legacyGetCertificatePublicImageUrl,
    legacyGetLinkedinCredential,
    legacyGetLinkedinOrganization,
    legacyGetOrganization,
    legacyGetPage,
    legacyGetPublicLearningRouteCertificates,
    legacyGetValidatedCertificate,
    legacyGetValidatedCertificateAsset,
    legacyLinkedinShare,
    legacyOpenGraphShare,
    legacyPendingApprovalCertificates,
    legacyRequestOTC,
    LegacyTrackCategory as TrackCategory,
    legacyUpdateShareCode,
    legacyValidateCertificateAccessToken,
    legacyValidateCertificateIdentification,
} from '@pok/viewer-api-sdk';
import axiosRetry from 'axios-retry';
import { Buffer } from 'buffer';
import { anonymous } from '@pok/model';
import { trackActivity } from './utils/custom-track-events.js';

const certificatesContractAddress = import.meta.env.VITE_CERTIFICATES_CONTRACT;

const chainTools = new Alchemy({
    network: Network[import.meta.env.VITE_ALCHEMY_NETWORK],
}).config
    .getProvider()
    .then((provider) =>
        provider.getNetwork().then((network) => {
            return {
                thirdParties: webLib.thirdParties({
                    chainId: network.chainId,
                    certificatesContractAddress: certificatesContractAddress,
                }),
                proofsLib: webLib.proofs(proofs, `${import.meta.env.VITE_VIEWER_API_ROOT}/legacy/proofs-proxies`),
            };
        }),
    );

export const proofsOf = (wallet) => alchemyProofsOf(import.meta.env.VITE_VIEWER_API_ROOT, wallet);

export const polygonWalletLink = (chainId, wallet) =>
    chainId === 80002 ? `https://amoy.polygonscan.com/address/${wallet}` : `https://polygonscan.com/address/${wallet}`;

export const links = async () => chainTools.then((c) => c.thirdParties);

export const proofs = async () => chainTools.then((c) => c.proofsLib);

export const validateIdentification = async (certificate, identification) =>
    legacyValidateCertificateIdentification(import.meta.env.VITE_VIEWER_API_ROOT, certificate.id, identification);

export const certificateExists = async (id, retry) => {
    const client = axios.create();

    if (retry)
        axiosRetry(client, {
            retries: 25,
            retryCondition: () => true,
            retryDelay: () => 9000,
        });

    return getCertificateWithCustomAxios(client, import.meta.env.VITE_VIEWER_API_ROOT, id);
};

export const certificateDetails = async (id) =>
    legacyGetCertificate(import.meta.env.VITE_VIEWER_API_ROOT, id).then((cert) => ({
        id: cert.id,
        free: cert.free,
        tokenId: cert.token,
        image: gatewayUrl(cert.imageCid),
        title: cert.title,
        date: cert.issueDate,
        url: cert.url,
        institution: cert.institution,
        snapshot: cert.templateCid,
        minter: cert.minter,
        emissionType: cert.emissionType,
        customPage: cert.customPage,
        learningRoutes: cert.learningRoutes,
        templateType: cert.templateType,
        has2FValidation: cert.has2FValidation,
        currentState: cert.currentState,
        replacedById: cert.replacedById,
        assets: cert.assets,
        userId: cert.userId,
        expires: cert.expires,
        expired: cert.expired,
    }));

export const findPage = (minter, id) =>
    id
        ? legacyGetPage(import.meta.env.VITE_VIEWER_API_ROOT, id).then((p) => parsePage(minter, p))
        : Promise.resolve(emptyPage);

const parsePage = (wallet, page) => {
    return {
        header: {
            logo: applyGatewayIfApply(wallet, page.definition.header?.logo, page.assetsGatewayUrl),
            color: page.definition.header?.color ?? '#5124A6',
        },
        style: {
            color: page.definition.style?.color ?? '#5124A6',
            textButtonColor: page.definition.style?.textButtonColor ?? '#FFFFFF',
            tabColor: page.definition.style?.tabColor ?? '#5124A6',
        },
        banner: {
            image: applyGatewayIfApply(wallet, page.definition.banner?.image, page.assetsGatewayUrl),
            background: page.definition.banner?.background,
            cover: page.definition.banner?.cover,
            alignment: page.definition.banner?.alignment,
        },
        widgets: page.definition.widgets?.map((x) => ({
            ...x,
            payload: {
                ...x.payload,
                image: { url: applyGatewayIfApply(wallet, x.payload.image?.id, page.assetsGatewayUrl) },
            },
        })),
    };
};

export const emptyPage = {
    banner: {},
    header: {},
    style: { color: '#5124A6', tabColor: '5124A6', textButtonColor: '#FFFFFF' },
    widgets: [],
};

const applyGatewayIfApply = (wallet, id, gateway) => {
    if (id) return applyGateway(wallet, id, gateway);
    return undefined;
};

export const templateFromIpfs = async (cid) => axios.get(gatewayUrl(cid)).then((response) => response.data);

export const gatewayUrl = (ipfs) => gateway(ipfs, import.meta.env.VITE_IPFS_GATEWAY);

export const getLinkedinCredential = async (authorizationCode) =>
    legacyGetLinkedinCredential(import.meta.env.VITE_VIEWER_API_ROOT, authorizationCode).catch((e) =>
        Promise.reject(e.response.status === 401 ? new AuthorizationCodeExpired(authorizationCode) : e),
    );

export const createLinkedinShare = async (accessToken, certificate, title, commentary, postVisibility) =>
    legacyLinkedinShare(import.meta.env.VITE_VIEWER_API_ROOT, certificate.id, {
        linkedinAccessToken: accessToken,
        title: title,
        commentary: commentary,
        certificateUrl: certificate.url,
        visibility: postVisibility,
    });

export const linkedinOrganizationId = async (certificate) =>
    legacyGetLinkedinOrganization(
        import.meta.env.VITE_VIEWER_API_ROOT,
        getValidatedCertificateAccessToken(certificate.id),
    )
        .then((e) => e.id)
        .catch(() => '');

const createShare = async (accessToken, certificate, locale) =>
    legacyOpenGraphShare(import.meta.env.VITE_VIEWER_API_ROOT, certificate.id, {
        locale,
        accessToken,
    });

export const getShareOGUrl = async (certificate, locale) =>
    createShare(getValidatedCertificateAccessToken(certificate.id), certificate, locale).then((res) => res.shareUrl);

export class AuthorizationCodeExpired extends Error {
    constructor(authorizationCode) {
        super(`${authorizationCode} has expired`);
    }
}

export const findPendingCertificates = async (id, token) =>
    legacyPendingApprovalCertificates(import.meta.env.VITE_VIEWER_API_ROOT, id, token);

export const scheduleCertificate = async (id, token, suggestedShareCode) =>
    legacyApproveCertificateIssuance(import.meta.env.VITE_VIEWER_API_ROOT, id, token, suggestedShareCode);

export const validateAndLoadFromImageStore = async (certificateId, encryptionKey) =>
    legacyValidateCertificateAccessToken(import.meta.env.VITE_VIEWER_API_ROOT, encryptionKey, certificateId)
        .then((authResponse) => authResponse.accessToken)
        .then((accessToken) => {
            saveValidatedCertificateAccessToken(certificateId, accessToken);
            return legacyGetValidatedCertificate(import.meta.env.VITE_VIEWER_API_ROOT, certificateId, accessToken);
        })
        .then(async (base64image) => {
            trackActivity(
                TrackCategory.CERTIFICATE,
                CertificateTrackAction.VERIFY,
                certificateId,
                undefined,
                undefined,
            );
            return base64toBlob(base64image).then((blob) => URL.createObjectURL(blob));
        });

export const loadFromImageStore = async (certificateId, accessToken) =>
    Promise.resolve(accessToken)
        .then((accessToken) =>
            legacyGetValidatedCertificate(import.meta.env.VITE_VIEWER_API_ROOT, certificateId, accessToken),
        )
        .then(async (base64image) => {
            return base64toBlob(base64image).then((blob) => URL.createObjectURL(blob));
        })
        .catch(() => Promise.resolve());

export const encryptedCertificateImageUrl = (certificateId) => {
    return legacyGetCertificatePublicImageUrl(import.meta.env.VITE_VIEWER_API_ROOT, certificateId);
};

export const loadOrganization = async (organizationId) =>
    legacyGetOrganization(import.meta.env.VITE_VIEWER_API_ROOT, organizationId).catch(() => Promise.resolve());

export const loadLearningRouteCertificates = async (certificate, learningRoute) =>
    legacyGetPublicLearningRouteCertificates(import.meta.env.VITE_VIEWER_API_ROOT, certificate, learningRoute);

export const requestOTCtoChangeCertificate = async (certificateId, email) =>
    legacyRequestOTC(import.meta.env.VITE_VIEWER_API_ROOT, {
        certificateId: certificateId,
        email: email,
    });

export const validateOTCtoChangeCertificate = async (certificateId, otc) =>
    legacyChangeRequestDraft(import.meta.env.VITE_VIEWER_API_ROOT, {
        otc: otc,
        certificateId: certificateId,
    });

export const sendChangeRequest = async (certificateId, token, data) =>
    legacyChangeRequestCommit(import.meta.env.VITE_VIEWER_API_ROOT, {
        certificateId: certificateId,
        changeRequestId: token,
        firstName: data.first_name,
        lastName: data.last_name,
    });

export const forgotPassword = async (certificateId, email) =>
    legacyForgotCertificatePassword(import.meta.env.VITE_VIEWER_API_ROOT, certificateId, email);

export const getDecryptedAsset = async (certificateId, assetId, token) =>
    legacyGetValidatedCertificateAsset(
        import.meta.env.VITE_VIEWER_API_ROOT,
        certificateId,
        assetId,
        anonymous(token),
    ).then((response) => Buffer.from(response.data, 'binary'));

export const certificatesFor = async (user) => {
    return getCertificatesFromUser(import.meta.env.VITE_VIEWER_API_ROOT, user);
};
export const fetchDomainDetails = async (organizationId) => {
    return getDomainDetails(import.meta.env.VITE_VIEWER_API_ROOT, organizationId);
};

export const updateShareCode = async (input) => legacyUpdateShareCode(import.meta.env.VITE_VIEWER_API_ROOT, input);
